import type {
  DashboardMenuPluginConfig,
  DashboardPageConfig,
  DashboardModalConfig,
  DashboardPluginConfig,
} from '@wix/cli-app-definitions';
import {
  dashboardPageConfigSchema,
  dashboardModalConfigSchema,
  dashboardPluginConfigSchema,
  dashboardMenuPluginConfigSchema,
} from '@wix/cli-app-definitions';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { pathExists, readJson } from '@wix/cli-fs';
import { isType } from 'variant';
import { replaceExtension } from '../path.js';
import type { ProjectModel } from '../../../model.js';
import { DevCenterComponent } from '../dev-center-component.js';

export async function loadDashboardPageComponentConfig(
  path: string
): Promise<DashboardPageConfig> {
  const configPath = replaceExtension(path, 'json');
  const isConfigExist = await pathExists(configPath);

  if (!isConfigExist) {
    throw new CliError({
      code: CliErrorCode.DashboardPageConfigNotFound({ configPath }),
      cause: null,
    });
  }

  const config = await readJson(configPath);
  const parsedConfig = dashboardPageConfigSchema.safeParse(config);

  if (parsedConfig.success) {
    return parsedConfig.data;
  }

  throw new CliError({
    code: CliErrorCode.InvalidConfigSchemaError({
      configPath,
      zodError: parsedConfig.error,
    }),
    cause: parsedConfig.error,
  });
}

export async function loadDashboardPluginComponentConfig(
  pluginPath: string
): Promise<DashboardPluginConfig> {
  const configPath = replaceExtension(pluginPath, 'json');
  const isConfigExist = await pathExists(configPath);

  if (!isConfigExist) {
    throw new CliError({
      code: CliErrorCode.DashboardPluginConfigNotFound({ configPath }),
      cause: null,
    });
  }

  const config = await readJson(configPath);
  const parsedConfig = dashboardPluginConfigSchema.safeParse(config);

  if (parsedConfig.success) {
    return parsedConfig.data;
  }

  throw new CliError({
    code: CliErrorCode.InvalidConfigSchemaError({
      configPath,
      zodError: parsedConfig.error,
    }),
    cause: parsedConfig.error,
  });
}

export async function loadDashboardMenuPluginComponentConfig(
  menuPluginPath: string
): Promise<DashboardMenuPluginConfig> {
  const configPath = replaceExtension(menuPluginPath, 'json');
  const isConfigExist = await pathExists(configPath);

  if (!isConfigExist) {
    throw new CliError({
      code: CliErrorCode.DashboardMenuPluginConfigNotFound({ configPath }),
      cause: null,
    });
  }

  const config = await readJson(configPath);
  const parsedConfig = dashboardMenuPluginConfigSchema.safeParse(config);

  if (parsedConfig.success) {
    return parsedConfig.data;
  }

  throw new CliError({
    code: CliErrorCode.InvalidConfigSchemaError({
      configPath,
      zodError: parsedConfig.error,
    }),
    cause: parsedConfig.error,
  });
}

export async function loadDashboardModalComponentConfig(
  modalPath: string
): Promise<DashboardModalConfig> {
  const configPath = replaceExtension(modalPath, 'json');
  const isConfigExist = await pathExists(configPath);

  if (!isConfigExist) {
    throw new CliError({
      code: CliErrorCode.DashboardModalConfigNotFound({ configPath }),
      cause: null,
    });
  }

  const config = await readJson(configPath);
  const parsedConfig = dashboardModalConfigSchema.safeParse(config);

  if (parsedConfig.success) {
    return parsedConfig.data;
  }

  throw new CliError({
    code: CliErrorCode.InvalidConfigSchemaError({
      configPath,
      zodError: parsedConfig.error,
    }),
    cause: parsedConfig.error,
  });
}

export function virtualPathForHTMLAsset(componentId: string) {
  return `wix/html/dashboard-asset/${componentId}.html`;
}

export function virtualDashboardHTMLAssets(model: ProjectModel) {
  return new Map(
    model.components
      .filter(
        (
          component
        ): component is DevCenterComponent<
          'DashboardPage' | 'DashboardPlugin' | 'DashboardModal'
        > =>
          isType(component, DevCenterComponent.DashboardPage) ||
          isType(component, DevCenterComponent.DashboardPlugin) ||
          isType(component, DevCenterComponent.DashboardModal)
      )
      .map((comp) => [virtualPathForHTMLAsset(comp.id), comp])
  );
}

export function virtualModuleIdForScriptAsset(componentId: string) {
  return `wix/scripts/dashboard-asset/${componentId}.js`;
}

export function virtualDashboardScriptAssets(model: ProjectModel) {
  return new Map(
    model.components
      .filter(
        (
          component
        ): component is DevCenterComponent<
          'DashboardPage' | 'DashboardPlugin' | 'DashboardModal'
        > =>
          isType(component, DevCenterComponent.DashboardPage) ||
          isType(component, DevCenterComponent.DashboardPlugin) ||
          isType(component, DevCenterComponent.DashboardModal)
      )
      .map((comp) => [virtualModuleIdForScriptAsset(comp.id), comp])
  );
}
